import { useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationMembers } from '@/hooks/useOrganizationMembers';
import { getUserInitials } from '@/lib/userUtils';

export interface MemberIdentity {
  displayName: string | null;
  email: string | null;
}

/**
 * Resolves org-member user ids to a name + initials, for anywhere a row is
 * stamped with a `user_id` (comment authors, mention pills, activity rows).
 *
 * Built on the cached `useOrganizationMembers` query (the Team page's read of
 * `organization_members` + `profiles`), so it costs no extra request on pages
 * that already show members. Names come from `profiles.display_name` — the
 * preferred name the user set on their Profile page — for everyone including
 * the signed-in user, whose email stands in while the members query is still
 * loading. The session's `user_metadata` is never read: it is user-writable
 * and goes stale the moment the user renames themselves.
 */
export function useMemberDirectory(organizationId?: string) {
  const { user } = useAuth();
  const { data: members = [], isLoading } = useOrganizationMembers(organizationId);

  const byUserId = useMemo(() => {
    const map = new Map<string, MemberIdentity>();
    for (const m of members) {
      map.set(m.user_id, {
        displayName: m.profiles?.display_name ?? null,
        email: m.profiles?.email ?? null,
      });
    }
    return map;
  }, [members]);

  const identityOf = useCallback(
    (userId: string | null | undefined): MemberIdentity | null => {
      if (!userId) return null;
      if (userId === user?.id) {
        return {
          displayName: byUserId.get(userId)?.displayName ?? null,
          email: user.email ?? null,
        };
      }
      return byUserId.get(userId) ?? null;
    },
    [byUserId, user],
  );

  /** Name, then email, then `null` (caller picks its own placeholder). */
  const nameOf = useCallback(
    (userId: string | null | undefined): string | null => {
      const id = identityOf(userId);
      return id?.displayName || id?.email || null;
    },
    [identityOf],
  );

  const initialsOf = useCallback(
    (userId: string | null | undefined, fallback = '?'): string => {
      const id = identityOf(userId);
      return getUserInitials({ displayName: id?.displayName, email: id?.email, fallback });
    },
    [identityOf],
  );

  return { identityOf, nameOf, initialsOf, isLoading };
}
